/*
 * Termos de uso e politica de privacidade.
 *
 * Nao ha "editar": publicar cria uma versao nova e aposenta a anterior. O texto
 * antigo continua no banco porque e a ele que os aceites ja gravados se referem
 * — reescrever no lugar destruiria a prova de consentimento que sustenta a
 * coleta de nome e telefone.
 */
import { useEffect, useMemo, useState } from 'react';
import { ApiRequestError, apiRequest } from '../lib/api';
import type { TermDto } from '../lib/types';
import { Alert, Button, Field, Spinner } from './ui';

const TIPOS = [
  { chave: 'terms', rotulo: 'Termos de uso' },
  { chave: 'privacy', rotulo: 'Política de privacidade' },
] as const;

type Tipo = (typeof TIPOS)[number]['chave'];

interface Props {
  podePublicar: boolean;
}

export function TermsManager({ podePublicar }: Props) {
  const [documentos, setDocumentos] = useState<TermDto[] | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [editando, setEditando] = useState<Tipo | null>(null);

  useEffect(() => {
    void recarregar();
  }, []);

  async function recarregar() {
    try {
      setDocumentos(await apiRequest<TermDto[]>('/admin/terms'));
    } catch (caught) {
      setErro(caught instanceof ApiRequestError ? caught.displayMessage : 'Falha ao carregar');
    }
  }

  if (!documentos) return <Spinner label="Carregando documentos..." />;

  const semVigente = TIPOS.filter(
    ({ chave }) => !documentos.some((d) => d.kind === chave && d.isActive),
  );

  return (
    <div className="space-y-5">
      {erro ? <Alert>{erro}</Alert> : null}

      {semVigente.length > 0 ? (
        <Alert tone="warning">
          Sem texto vigente de {semVigente.map((t) => t.rotulo.toLowerCase()).join(' e ')}. O modo
          cadastro não funciona sem os dois — é o aceite que dá base legal para guardar o dado do
          cliente.
        </Alert>
      ) : null}

      {TIPOS.map(({ chave, rotulo }) => (
        <BlocoTipo
          key={chave}
          rotulo={rotulo}
          versoes={documentos.filter((d) => d.kind === chave)}
          podePublicar={podePublicar}
          editando={editando === chave}
          onEditar={() => setEditando(editando === chave ? null : chave)}
          onConcluir={() => {
            setEditando(null);
            void recarregar();
          }}
          tipo={chave}
        />
      ))}
    </div>
  );
}

function BlocoTipo({
  tipo,
  rotulo,
  versoes,
  podePublicar,
  editando,
  onEditar,
  onConcluir,
}: {
  tipo: Tipo;
  rotulo: string;
  versoes: TermDto[];
  podePublicar: boolean;
  editando: boolean;
  onEditar: () => void;
  onConcluir: () => void;
}) {
  const vigente = useMemo(() => versoes.find((v) => v.isActive) ?? null, [versoes]);
  const anteriores = versoes.filter((v) => !v.isActive);

  return (
    <div className="rounded-lg border border-slate-200">
      <header className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 px-4 py-3">
        <div>
          <h3 className="text-sm font-semibold text-slate-800">{rotulo}</h3>
          {vigente ? (
            <p className="text-xs text-slate-500">
              Vigente: versão {vigente.version} · {vigente.acceptanceCount}{' '}
              {vigente.acceptanceCount === 1 ? 'aceite' : 'aceites'}
            </p>
          ) : (
            <p className="text-xs text-red-700">Nenhuma versão vigente.</p>
          )}
        </div>

        {podePublicar ? (
          <Button variant="ghost" onClick={onEditar}>
            {editando ? 'Cancelar' : 'Publicar nova versão'}
          </Button>
        ) : null}
      </header>

      <div className="space-y-4 p-4">
        {editando ? (
          <FormularioPublicacao tipo={tipo} base={vigente} onConcluir={onConcluir} />
        ) : vigente ? (
          <details>
            <summary className="cursor-pointer text-sm font-medium text-slate-700">
              Ver o texto vigente
            </summary>
            <pre className="mt-2 max-h-72 overflow-auto rounded bg-slate-50 p-3 text-xs whitespace-pre-wrap text-slate-700">
              {vigente.bodyMd}
            </pre>
          </details>
        ) : null}

        {anteriores.length > 0 ? (
          <div>
            <p className="mb-1.5 text-xs font-medium tracking-wide text-slate-500 uppercase">
              Versões anteriores
            </p>
            <ul className="space-y-1.5">
              {anteriores.map((versao) => (
                <li
                  key={versao.id}
                  className="flex flex-wrap items-center justify-between gap-2 text-sm"
                >
                  <span className="text-slate-700">
                    Versão {versao.version}
                    <span className="ml-2 text-xs text-slate-500">
                      {versao.acceptanceCount}{' '}
                      {versao.acceptanceCount === 1 ? 'aceite' : 'aceites'}
                    </span>
                  </span>
                  {podePublicar ? <BotaoReativar id={versao.id} onConcluir={onConcluir} /> : null}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function BotaoReativar({ id, onConcluir }: { id: number; onConcluir: () => void }) {
  const [ocupado, setOcupado] = useState(false);

  return (
    <Button
      variant="ghost"
      disabled={ocupado}
      onClick={() => {
        setOcupado(true);
        void apiRequest(`/admin/terms/${id}/activate`, { method: 'POST' })
          .then(onConcluir)
          .finally(() => setOcupado(false));
      }}
    >
      Voltar a usar
    </Button>
  );
}

function FormularioPublicacao({
  tipo,
  base,
  onConcluir,
}: {
  tipo: Tipo;
  base: TermDto | null;
  onConcluir: () => void;
}) {
  // Parte do texto vigente: publicar quase sempre e ajustar o que ja existe.
  const [titulo, setTitulo] = useState(base?.title ?? '');
  const [versao, setVersao] = useState(proximaVersao(base?.version));
  const [corpo, setCorpo] = useState(base?.bodyMd ?? '');
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  async function publicar() {
    setSalvando(true);
    setErro(null);
    try {
      await apiRequest('/admin/terms', {
        method: 'POST',
        body: { kind: tipo, version: versao.trim(), title: titulo.trim(), bodyMd: corpo },
      });
      onConcluir();
    } catch (caught) {
      setErro(caught instanceof ApiRequestError ? caught.displayMessage : 'Falha ao publicar');
    } finally {
      setSalvando(false);
    }
  }

  return (
    <div className="space-y-3">
      <Alert tone="warning">
        Ao publicar, <strong>todos os clientes verão a tela de aceite de novo</strong> na próxima
        conexão, inclusive quem está dentro do prazo de cortesia. É o comportamento correto — o
        aceite anterior se referia a outro texto.
      </Alert>

      {erro ? <Alert>{erro}</Alert> : null}

      <div className="grid gap-3 sm:grid-cols-[1fr_140px]">
        <Field
          label="Título"
          value={titulo}
          onChange={(e) => setTitulo(e.target.value)}
          hint="É o que o cliente vê como link no portal."
        />
        <Field
          label="Versão"
          value={versao}
          onChange={(e) => setVersao(e.target.value)}
          hint="Ex.: 1.1"
        />
      </div>

      <label className="block">
        <span className="mb-1.5 block text-sm font-medium text-slate-700">Texto</span>
        <textarea
          value={corpo}
          onChange={(e) => setCorpo(e.target.value)}
          rows={18}
          className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 font-mono text-xs shadow-sm focus:border-brand-500 focus:ring-2 focus:ring-brand-100 focus:outline-none"
        />
        <span className="mt-1 block text-xs text-slate-500">
          Aceita títulos com <code># </code> e <code>## </code> e itens de lista com{' '}
          <code>- </code>. O portal exibe como texto, nunca como HTML.
        </span>
      </label>

      <div className="flex justify-end">
        <Button onClick={() => void publicar()} disabled={salvando || corpo.trim().length < 50}>
          {salvando ? 'Publicando...' : 'Publicar e tornar vigente'}
        </Button>
      </div>
    </div>
  );
}

/** Sugere o proximo numero: "1.0" vira "1.1". Editavel a vontade. */
function proximaVersao(atual: string | undefined): string {
  if (!atual) return '1.0';

  const partes = atual.split('.');
  const ultima = Number(partes.at(-1));
  if (!Number.isFinite(ultima)) return atual;

  return [...partes.slice(0, -1), String(ultima + 1)].join('.');
}
